// default theme preset

export const preset = {
  breakpoints: [`576px`, `768px`, `992px`, `1200px`, `1441px`, `1600px`],
  colors: {
    text: "#000",
    background: "#fff",
    primary: "#340ef1",
    "primary-dark": "#2906d6",
    secondary: "#e2dc23",
    muted: "#f6f6f9",
    gray: "#dddddf",
    "gray-light": "#e0e0e0",
    highlight: "hsla(205, 100%, 40%, 0.125)",
  },
  fonts: {
    body: "NimbusSanL, system-ui, sans-serif",
    heading: "Space Mono",
    monospace: "Space Mono, Menlo, monospace",
  },
  fontSizes: [14, 16, 19, 20, 26, 42, 82],
  fontSizeBase: 19,
  headings: {
    h1: {
      fontSize: 82,
    },
    h2: {
      fontSize: 42,
      lineHeight: 1,
    },
    h3: {
      fontSize: 26,
      lineHeight: 1.192307692,
    },
    h4: {
      fontSize: 20,
      lineHeight: 1.2,
    },
    h5: {
      fontSize: 19,
    },
    h6: {
      fontSize: 16,
      lineHeight: 1.1875,
    },
  },
  fontWeights: {
    body: 400,
    heading: 700,
    bold: 700,
  },
  lineHeights: {
    body: 1.578947368,
    heading: 1.25,
  },
  space: [0, 4, 8, 16, 32, 60, 120, 256, 512],
  sizes: {
    avatar: 48,
  },
  radii: {
    small: 4,
    default: 8,
    circle: '100%',
  },
  shadows: {
    card: "0 0 4px rgba(0, 0, 0, .125)",
  },
  // rebass variants
  text: {
    heading: {
      fontFamily: "heading",
      lineHeight: "heading",
      fontWeight: "heading",
    },
    display: {
      fontFamily: "heading",
      fontWeight: "heading",
      lineHeight: "heading",
      fontSize: [6, 7, 8],
    },
    caps: {
      textTransform: "uppercase",
      letterSpacing: "0.1em",
    },
  },
  variants: {
    avatar: {
      width: "avatar",
      height: "avatar",
      borderRadius: "circle",
    },
    card: {
      p: 2,
      bg: "background",
      boxShadow: "card",
    },
    nav: {
      fontSize: 1,
      fontWeight: "bold",
      display: "inline-block",
      p: 2,
      color: "inherit",
      textDecoration: "none",
      ":hover,:focus,.active": {
        color: "primary",
      },
    },
  },
  buttons: {
    primary: {
      fontSize: [0, 1],
      paddingTop: '19px',
      paddingRight: ['20px', null, '24px'],
      paddingBottom: '18px',
      paddingLeft: ['20px', null, '24px'],
      color: "background",
      bg: "primary",
      borderRadius: "small",
      fontFamily: "heading",
      lineHeight: 1.1875,
    },
    baseLink: {
      background: "transparent",
      color: "currentColor",
      fontSize: [0,1],
      fontFamily: "heading",
      lineHeight: 1.1875,
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 1,
      paddingBottom: 1,
      position: "relative",
      "::before": {
        content: "''",
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "1px",
        bg: "currentColor",
        transformOrigin: "top left",
        animationName: "buttonsBaseLinkOut",
        animationDuration: "850ms",
        animationTimingFunction: "cubic-bezier(0.475,0.425,0,0.995)",
        animationFillMode: "forwards",
      },
      ":hover,:focus,:active": {
        "::before": {
          animationName: "buttonsBaseLinkHover",
          animationDuration: "850ms",
          animationTimingFunction: "cubic-bezier(0.475,0.425,0,0.995)",
          animationFillMode: "both",
        },
      },
    },
    link: {
      variant: "buttons.baseLink",
      color: "primary",
      "::after": {
        content: "'↗'",
        display: "inline-block",
        color: "text",
        paddingLeft: 2,
      },
    },
    "link-inverted": {
      variant: "buttons.baseLink",
      color: "currentColor",
      "::after": {
        content: "'↗'",
        display: "inline-block",
        color: "currentColor",
        paddingLeft: 2,
      },
    },
    faq: {
      background: "transparent",
      color: "text",
      cursor: "pointer",
      display: "flex",
      justifyContent: "space-between",
      alignItems: ["flex-start", "center"],
      fontFamily: "body",
      fontSize: ["18px", "18px", 2],
      fontWeight: "bold",
      lineHeight: 1.263157895,
      paddingTop: ["20px", "20px", "24px"],
      paddingBottom: ["20px", "20px", "24px"],
      paddingLeft: ["14px", "14px", "30px"],
      paddingRight: "14px",
      textAlign: "left",
      width: "100%",
      "::after": {
        marginLeft: "18px",
        content: "'↓'",
        fontFamily: "heading",
        transformOrigin: "center center",
        transform: "rotate(0deg)",
        transition: "transform 250ms cubic-bezier(0.215, 0.610, 0.355, 1.000)",
        fontWeight: 300
      },
      "&.--selected::after": {
        transform: "rotate(180deg)",
      },
      ":hover,:focus,:active": {
        color: "primary",
        outline: "none"
      }
    },
    "cta-filled": {
      variant: "buttons.primary",
      color: "white",
      bg: "primary",
      ":hover,:focus,:active": {
        bg: "primary-dark",
      },
    },
    "cta-outlined": {
      variant: "buttons.primary",
      color: "primary",
      bg: "transparent",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "primary",
      position: "relative",
      paddingTop: ['16px', null, '11px'],
      paddingBottom: ['16px', null, '11px'],
      overflow: "hidden",
      transition: "color 0ms 350ms linear",
      "::before": {
        content: "''",
        position: "absolute",
        zIndex: -1,
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bg: "primary",
        transformOrigin: "top left",
        animationName: "buttonsCtaOutlinedOut",
        animationDuration: "450ms",
        animationTimingFunction: "cubic-bezier(1,0.005,0.525,0.575)",
        animationFillMode: "forwards",
      },
      ":hover,:focus,:active": {
        color: "background",
        transition: "color 0ms 100ms linear",

        "::before": {
          animationName: "buttonsCtaOutlinedHover",
          animationDuration: "450ms",
          animationTimingFunction: "cubic-bezier(0.475,0.425,0,0.995)",
          animationFillMode: "both",
        }
      }
    },
    tabs: {
      variant: "buttons.primary",
      color: "text",
      bg: "transparent",
      border: "1px solid",
      borderColor: "gray-light",
      borderRadius: "small",
      cursor: "pointer",
      lineHeight: 1.578947368,
      fontFamily: "body",
      fontSize: "19px",
      paddingTop: ["20px", "20px", 4],
      paddingRight: ["24px", "24px", "46px"],
      paddingBottom: ["20px", "20px", 4],
      paddingLeft: ["24px", "24px", "46px"],
      textAlign: "left",
      transition: "border-color linear 0.5s",
      ":hover,:focus,:active": {
        borderColor: "#3426f1",
      },
    },
    secondary: {
      variant: "buttons.primary",
      color: "background",
      bg: "secondary",
    },
  },
  forms: {
    input: {
      backgroundColor: "body",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "#E0E0E0",
      borderRadius: "small",
      fontFamily: "heading",
      fontSize: 1,
      fontWeight: "body",
      lineHeight: 1.1875,
      paddingTop: "18px",
      paddingBottom: "18px",
      paddingLeft: "24px",
      paddingRight: "24px",
      margin: 0,
      ":focus": {
        borderColor: "primary",
        boxShadow: "none",
        outline: "none",
      }
    },
    select: {
      variant: "forms.input",
      "& + svg": {
        color: "primary",
      }
    },
    label: {
      color: "#4A494A",
      fontFamily: "heading",
      fontSize: 1,
      fontWeight: "body",
      lineHeight: 1.1875,
    },
    button: {
      bg: "red",
    }
  },
  styles: {
    root: {
      fontFamily: "body",
      fontWeight: "body",
      lineHeight: "body",
    },
  },
}

export default preset
